import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import EntityCard from '../../ui/EntityCard';
import { apiClient } from '../../../services/api/apiService';
import tournamentService from '../../../services/api/tournamentService';
import { eid } from '../../../utils/entityId';
import { membersLine, onCourtSize } from '../../../utils/teamCapacity';
import { openLeagueEntry } from '../leagueStats';
import UserSearchInput from '../../match/UserSearchInput';
import { getLeagueFormat, isUserEntered } from '../leagueFormat';
import { isFinalStatus, isRegistrationOpen, normalizeLeagueStatus } from '../leagueStatus';
import WithdrawEntryDialog from './WithdrawEntryDialog';

/**
 * LeagueTeamsTab
 *
 * Host (canManageLeagues):
 *   - Invite search (only when league is active)
 *   - Pending invitations panel to accept/reject
 *   - List of enrolled teams with remove option
 *
 * Non-host + public + active:
 *   - "Request to Join" panel
 *
 * Non-host + private:
 *   - Locked 🔒 card
 *
 * Singles leagues (participant_format 'individual') invite and enter players;
 * doubles leagues ('pair') invite and enter two-player pairs.
 */
const LeagueTeamsTab = ({
  league,
  teams,
  user,
  canManageLeagues,
  showFeedback,
  openConfirm,
  fetchLeagueTeams,
  loading,
}) => {
  const navigate = useNavigate();

  // ── Host invite search ──────────────────────────────────────────────────────
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteSearching, setInviteSearching] = useState(false);
  const [invitingTeamId, setInvitingTeamId] = useState(null);

  // ── Host pending invitations panel ─────────────────────────────────────────
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [pendingOutgoingIds, setPendingOutgoingIds] = useState(new Set()); // team ids with pending host-invite
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [respondingId, setRespondingId] = useState(null);

  // ── Non-host join request ───────────────────────────────────────────────────
  const [myTeams, setMyTeams] = useState([]);
  const [alreadyRegisteredTeams, setAlreadyRegisteredTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [joinMessage, setJoinMessage] = useState('');
  const [requestSending, setRequestSending] = useState(false);

  // ── Singles / doubles ───────────────────────────────────────────────────────
  const [pairPlayer1, setPairPlayer1] = useState(null);
  const [pairPlayer2, setPairPlayer2] = useState(null);
  const [partner, setPartner] = useState(null);
  const [myInvitation, setMyInvitation] = useState(null);
  const [partnerRequests, setPartnerRequests] = useState([]);

  // ── Remove team modal ───────────────────────────────────────────────────────
  const [removeConfirm, setRemoveConfirm] = useState({ isOpen: false, team: null, message: '', isLoading: false });
  const closeRemoveConfirm = () => setRemoveConfirm({ isOpen: false, team: null, message: '', isLoading: false });
  // Withdraw an entry from a running league (PR 9)
  const [withdrawing, setWithdrawing] = useState(null);

  // ── Derived flags ───────────────────────────────────────────────────────────
  // One lifecycle (leagueStatus.js): invitations and join requests only while
  // registration is open; "active" is the legacy name of in progress (LG-B11)
  const status = normalizeLeagueStatus(league?.status);
  const isActive = isRegistrationOpen(league);
  const isRegClosed = status === 'registration_closed';
  const isInProgress = status === 'in_progress';
  const isCompleted = status === 'completed' || status === 'cancelled';
  // no new entries once registration closes (schedule being built or games ongoing)
  const isLocked = isRegClosed || isInProgress || isCompleted;
  // Removal only before the season: once it runs, a team is withdrawn instead (409)
  const canRemoveEntries = canManageLeagues && !isFinalStatus(status) && !isInProgress;
  const isWithdrawn = (team) => String(team.playoff_status || '').startsWith('withdrawn');
  const canWithdraw = (team) => isInProgress && !isWithdrawn(team) && (canManageLeagues
    || (user?.id != null && [team.user_id, team.created_by, team.owner_id].some((id) => id != null && Number(id) === Number(user.id))));
  const isPublic = league?.is_public !== false;
  const isLeagueFull = league?.total_teams != null && teams.length >= league.total_teams;
  const canRequestJoin = !canManageLeagues && isActive && isPublic;
  const showLocked = !canManageLeagues && !isPublic;
  const { isSingles, isDoubles, unit, unitPlural, unitLower } = getLeagueFormat(league);
  const alreadyEntered = isUserEntered(teams, user?.id);
  // UserSearchInput re-searches when excludeUserIds changes identity, so keep these stable
  const enteredUserIds = useMemo(
    () => teams.flatMap((t) => (t.user_id ? [Number(t.user_id)] : (t.member_user_ids || []).map(Number))),
    [teams]
  );
  const excludeForPlayer1 = useMemo(() => [...enteredUserIds, ...(pairPlayer2 ? [pairPlayer2.id] : [])], [enteredUserIds, pairPlayer2]);
  const excludeForPlayer2 = useMemo(() => [...enteredUserIds, ...(pairPlayer1 ? [pairPlayer1.id] : [])], [enteredUserIds, pairPlayer1]);
  const excludeForPartner = useMemo(() => [user?.id, ...enteredUserIds], [user?.id, enteredUserIds]);

  // ── Load pending invitations for host ───────────────────────────────────────
  const fetchPendingInvitations = useCallback(async () => {
    if (!canManageLeagues || !league?.id) return;
    setInvitationsLoading(true);
    try {
      // Fetch join requests (team → host) and outgoing invites (host → team) in parallel
      const [joinRes, outgoingRes] = await Promise.all([
        tournamentService.getLeagueInvitations(league.id, 'pending'),       // join_request only
        apiClient.get(`/leagues/${league.id}/invitations?status=pending&type=invite`).catch(() => ({ data: { data: [] } })),
      ]);
      setPendingInvitations(joinRes?.data || []);
      const outgoing = Array.isArray(outgoingRes?.data?.data)
        ? outgoingRes.data.data
        : Array.isArray(outgoingRes?.data) ? outgoingRes.data : [];
      // Teams by id, players (singles invitations) as "u<userId>"
      setPendingOutgoingIds(new Set(outgoing.map((i) => (i.user_id ? `u${i.user_id}` : i.team_id))));
    } catch {
      setPendingInvitations([]);
      setPendingOutgoingIds(new Set());
    } finally {
      setInvitationsLoading(false);
    }
  }, [canManageLeagues, league?.id]);

  useEffect(() => {
    fetchPendingInvitations();
  }, [fetchPendingInvitations]);

  // ── Singles: the signed-in player's pending invitation ─────────────────────
  const fetchMyInvitation = useCallback(async () => {
    if (!isSingles || canManageLeagues || !league?.id || !user?.id) { setMyInvitation(null); return; }
    setMyInvitation(await tournamentService.getMyLeagueInvitation(league.id));
  }, [isSingles, canManageLeagues, league?.id, user?.id]);

  useEffect(() => {
    fetchMyInvitation();
  }, [fetchMyInvitation]);

  // ── Doubles: players who asked me to be their partner (LG-B16) ─────────────
  const fetchPartnerRequests = useCallback(async () => {
    if (!isDoubles || canManageLeagues || !league?.id || !user?.id) { setPartnerRequests([]); return; }
    setPartnerRequests(await tournamentService.getLeaguePartnerRequests(league.id));
  }, [isDoubles, canManageLeagues, league?.id, user?.id]);

  useEffect(() => {
    fetchPartnerRequests();
  }, [fetchPartnerRequests]);

  const handleRespondPartner = async (request, action) => {
    setRespondingId(`p${request.team_id}`);
    try {
      await tournamentService.respondToLeaguePartnerRequest(league.id, request.team_id, action);
      showFeedback(action === 'accept' ? 'success' : 'info',
        action === 'accept'
          ? `👥 You are paired with ${request.requested_by_name || request.requested_by_username}. The host will review your request.`
          : 'Partner request declined.');
      fetchPartnerRequests();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to respond');
    } finally { setRespondingId(null); }
  };

  // ── Load user's own teams (for join-request panel) ──────────────────────────
  useEffect(() => {
    if (!canRequestJoin || !user?.id || isSingles || isDoubles) return;
    // Use /teams/my-teams to get only teams this user is actually a member of.
    // The generic GET /teams ignores created_by_me and returns global results.
    apiClient.get('/teams/my-teams')
      .then((res) => {
        const raw = res.data?.data;
        const list = Array.isArray(raw?.teams) ? raw.teams : Array.isArray(raw) ? raw : [];

        // Only consider teams the user owns/created — not teams they're merely a member of.
        // Being a scorer/player on another enrolled team must not block registering your own team.
        const ownedTeams = list.filter(
          (t) => t.created_by === user.id || t.member_role === 'owner'
        );

        // Filter by the league's sport type (case-insensitive)
        const leagueSport = (league?.sport_type || '').toLowerCase().trim();
        const sportMatching = leagueSport
          ? ownedTeams.filter((t) => (t.sport_type || t.sport || '').toLowerCase().trim() === leagueSport)
          : ownedTeams;

        const alreadyIn = new Set(teams.map((t) => t.team_id ?? t.id));

        // Teams that already belong to this league (block duplicate registration)
        const registered = sportMatching.filter((t) => alreadyIn.has(t.id));
        // Eligible teams: correct sport + owned + not yet enrolled
        const eligible = sportMatching.filter((t) => !alreadyIn.has(t.id));

        setAlreadyRegisteredTeams(registered);
        setMyTeams(eligible);
      })
      .catch(() => { setMyTeams([]); setAlreadyRegisteredTeams([]); });
  }, [canRequestJoin, user?.id, teams, league?.sport_type, isSingles, isDoubles]);

  // ── Host: invite team ───────────────────────────────────────────────────────
  const handleInviteSearch = async (query) => {
    setInviteSearch(query);
    if (!query || query.trim().length < 2) { setInviteResults([]); return; }
    setInviteSearching(true);
    try {
      const sportParam = league?.sport_type ? `&sport=${encodeURIComponent(league.sport_type)}` : '';
      const res = await apiClient.get(`/teams?search=${encodeURIComponent(query.trim())}&limit=8${sportParam}`);
      const raw = res.data?.data;
      setInviteResults(Array.isArray(raw) ? raw : Array.isArray(raw?.teams) ? raw.teams : []);
    } catch { setInviteResults([]); }
    finally { setInviteSearching(false); }
  };

  const handleInviteTeam = async (teamId, teamName) => {
    const alreadyMember = teams.some((t) => (t.team_id ?? t.id) === teamId);
    if (alreadyMember) { showFeedback('warning', `${teamName} is already in this league.`); return; }
    setInvitingTeamId(teamId);
    try {
      await tournamentService.inviteTeamToLeague(league.id, teamId);
      showFeedback('success', `Invitation sent to ${teamName}`);
      setInviteSearch(''); setInviteResults([]);
      fetchPendingInvitations();
    } catch (err) {
      const msg = err.message || '';
      // "already has a pending" is now prevented by UPSERT, but keep guard for safety
      showFeedback(/pending/i.test(msg) ? 'warning' : 'error', msg || 'Failed to send invitation');
    } finally { setInvitingTeamId(null); }
  };

  // ── Host: invite a player (singles) or a pair of players (doubles) ──────────
  const playerName = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(' ') || u?.username || 'Player';

  const handleInvitePlayer = async (player) => {
    if (enteredUserIds.includes(Number(player.id))) { showFeedback('warning', `${playerName(player)} is already in this league.`); return; }
    setInvitingTeamId(`u${player.id}`);
    try {
      await tournamentService.invitePlayerToLeague(league.id, player.id);
      showFeedback('success', `Invitation sent to ${playerName(player)}`);
      fetchPendingInvitations();
    } catch (err) {
      showFeedback(/pending|already/i.test(err.message || '') ? 'warning' : 'error', err.message || 'Failed to send invitation');
    } finally { setInvitingTeamId(null); }
  };

  const handleInvitePair = async () => {
    if (!pairPlayer1 || !pairPlayer2) { showFeedback('warning', 'Pick both players of the pair.'); return; }
    setInvitingTeamId('pair');
    try {
      await tournamentService.invitePairToLeague(league.id, [pairPlayer1.id, pairPlayer2.id]);
      showFeedback('success', `Invitation sent to ${playerName(pairPlayer1)} & ${playerName(pairPlayer2)}`);
      setPairPlayer1(null); setPairPlayer2(null);
      fetchPendingInvitations();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to send invitation');
    } finally { setInvitingTeamId(null); }
  };

  // ── Singles: the invited player answers ─────────────────────────────────────
  const handleRespondMyInvitation = async (action) => {
    if (!myInvitation) return;
    setRespondingId(myInvitation.id);
    try {
      await tournamentService.respondToLeaguePlayerInvitation(league.id, myInvitation.id, action);
      showFeedback(action === 'accept' ? 'success' : 'info',
        action === 'accept' ? `✅ You have joined ${league.name}.` : 'Invitation declined.');
      setMyInvitation(null);
      if (action === 'accept') fetchLeagueTeams();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to respond');
    } finally { setRespondingId(null); }
  };

  // ── Host: respond to invitation / join-request ──────────────────────────────
  const handleRespond = async (invId, action, teamName) => {
    setRespondingId(invId);
    try {
      await tournamentService.hostRespondToLeagueInvitation(league.id, invId, action);
      showFeedback(
        action === 'accept' ? 'success' : 'info',
        action === 'accept' ? `✅ ${teamName} has been added to the league.` : `❌ Request from ${teamName} declined.`
      );
      fetchPendingInvitations();
      if (action === 'accept') fetchLeagueTeams();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to respond');
    } finally { setRespondingId(null); }
  };

  // ── Non-host: send join request ─────────────────────────────────────────────
  const handleJoinRequest = async (e) => {
    e.preventDefault();
    if (!selectedTeamId) { showFeedback('warning', 'Please select a team to request with.'); return; }
    setRequestSending(true);
    try {
      await tournamentService.requestToJoinLeague(league.id, parseInt(selectedTeamId, 10), joinMessage);
      showFeedback('success', '📩 Join request sent! The host will review your request.');
      setSelectedTeamId(''); setJoinMessage('');
    } catch (err) {
      showFeedback('error', err.message || 'Failed to send join request');
    } finally { setRequestSending(false); }
  };

  // ── Non-host: join as a player (singles) or with a partner (doubles) ───────
  const handleJoinAsPlayerOrPair = async (e) => {
    e.preventDefault();
    if (isDoubles && !partner) { showFeedback('warning', 'Pick your doubles partner.'); return; }
    setRequestSending(true);
    try {
      if (isDoubles) {
        const res = await tournamentService.requestToJoinLeagueAsPair(league.id, partner.id, joinMessage);
        // A new pair: the partner confirms first, then the request goes to the host
        showFeedback('success', res?.data?.status === 'awaiting_partner'
          ? `📩 ${playerName(partner)} has been asked to confirm the pair. The request goes to the host once they accept.`
          : '📩 Join request sent! The host will review your request.');
      } else {
        await tournamentService.requestToJoinLeagueAsPlayer(league.id, joinMessage);
        showFeedback('success', '📩 Join request sent! The host will review your request.');
      }
      setPartner(null); setJoinMessage('');
    } catch (err) {
      showFeedback('error', err.message || 'Failed to send join request');
    } finally { setRequestSending(false); }
  };

  // ── Remove team ─────────────────────────────────────────────────────────────
  const confirmRemoveTeam = async () => {
    if (!removeConfirm.team) return;
    setRemoveConfirm((prev) => ({ ...prev, isLoading: true }));
    try {
      const teamActualId = removeConfirm.team.team_id || removeConfirm.team.id;
      await apiClient.delete(
        `/leagues/${eid(league) || league.id}/teams/${teamActualId}`,
        { data: { message: removeConfirm.message || undefined } }
      );
      showFeedback('success', `${unit} removed from league.`);
      fetchLeagueTeams();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message;
      if (error.response?.status === 409 || /schedule|match/i.test(errorMsg)) {
        openConfirm({ title: `Cannot Remove ${unit}`, message: errorMsg, confirmText: 'OK', isDanger: false, onConfirm: () => {} });
      } else {
        showFeedback('error', `Failed to remove ${unitLower}: ${errorMsg}`);
      }
    } finally { closeRemoveConfirm(); }
  };

  // ── Shared styles ───────────────────────────────────────────────────────────
  const panelStyle = { marginBottom: '1.25rem', padding: '1rem', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '0.5rem' };
  const inputStyle = { width: '100%', padding: '0.55rem 0.85rem', background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(99,102,241,0.4)', borderRadius: '0.4rem', color: '#e2e8f0', fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none' };
  const btnBase = { padding: '0.25rem 0.7rem', borderRadius: '0.3rem', cursor: 'pointer', fontSize: '0.78rem', border: '1px solid' };

  return (
    <div className="teams-content">
      <div className="content-header">
        <h3>League {unitPlural}</h3>
      </div>

      {/* ── SINGLES: the signed-in player was invited ──────────────────────── */}
      {myInvitation && (
        <div style={{ ...panelStyle, background: 'rgba(34,197,94,0.08)', borderColor: 'rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ color: '#86efac', fontSize: '0.9rem' }}>
            🏅 You have been invited to play in <strong>{league.name}</strong>.
            {myInvitation.message && <div style={{ color: '#94a3b8', fontStyle: 'italic', marginTop: '0.25rem' }}>&#8220;{myInvitation.message}&#8221;</div>}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => handleRespondMyInvitation('accept')}
              disabled={respondingId === myInvitation.id}
              style={{ ...btnBase, padding: '0.35rem 0.9rem', background: 'rgba(34,197,94,0.2)', borderColor: 'rgba(34,197,94,0.5)', color: '#22c55e' }}
            >
              ✅ Accept
            </button>
            <button
              onClick={() => handleRespondMyInvitation('decline')}
              disabled={respondingId === myInvitation.id}
              style={{ ...btnBase, padding: '0.35rem 0.9rem', background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.4)', color: '#ef4444' }}
            >
              ❌ Decline
            </button>
          </div>
        </div>
      )}

      {/* ── DOUBLES: a player asked me to be their partner ─────────────────── */}
      {partnerRequests.map((request) => (
        <div key={request.team_id} style={{ ...panelStyle, background: 'rgba(34,197,94,0.08)', borderColor: 'rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ color: '#86efac', fontSize: '0.9rem' }}>
            👥 <strong>{request.requested_by_name || request.requested_by_username}</strong> asked you to play <strong>{league.name}</strong> as a pair ({request.team_name}).
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => handleRespondPartner(request, 'accept')}
              disabled={respondingId === `p${request.team_id}`}
              style={{ ...btnBase, padding: '0.35rem 0.9rem', background: 'rgba(34,197,94,0.2)', borderColor: 'rgba(34,197,94,0.5)', color: '#22c55e' }}
            >
              ✅ Accept
            </button>
            <button
              onClick={() => handleRespondPartner(request, 'decline')}
              disabled={respondingId === `p${request.team_id}`}
              style={{ ...btnBase, padding: '0.35rem 0.9rem', background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.4)', color: '#ef4444' }}
            >
              ❌ Decline
            </button>
          </div>
        </div>
      ))}

      {/* ── LOCKED: private league, non-host ───────────────────────────────── */}
      {showLocked && (
        <div style={{ ...panelStyle, background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.3)', textAlign: 'center', padding: '2rem 1rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔒</div>
          <div style={{ color: '#94a3b8', fontWeight: 600, fontSize: '1rem' }}>Private League</div>
          <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.4rem' }}>
            This is an invite-only league. Only the host can add {unitLower}s.
          </div>
        </div>
      )}

      {/* ── HOST: invite + pending side-by-side (responsive 2-col) ────────── */}
      {canManageLeagues && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '1.25rem' }}>

          {/* Left: Invite search */}
          <div style={{ flex: '1 1 260px', minWidth: 0, padding: '1rem', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '0.5rem' }}>
            <div style={{ fontWeight: 600, color: '#a78bfa', marginBottom: '0.6rem', fontSize: '0.9rem' }}>
              🔍 Invite a {unit}
              {isLocked && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '0.25rem', padding: '0.1rem 0.4rem' }}>
                  🔒 {isRegClosed ? 'Registration closed' : `League ${isCompleted ? 'finished' : 'in progress'}`} — invites locked
                </span>
              )}
              {!isLocked && !isActive && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#f59e0b', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: '0.25rem', padding: '0.1rem 0.4rem' }}>
                  ⚠️ Open registration to send invites
                </span>
              )}
              {!isLocked && isActive && isLeagueFull && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '0.25rem', padding: '0.1rem 0.4rem' }}>
                  🚫 League is full
                </span>
              )}
            </div>
            {isLocked ? (
              <div style={{ color: '#64748b', fontSize: '0.85rem', padding: '0.5rem 0' }}>
                {unitPlural} cannot be invited once {isRegClosed ? 'registration is closed' : `the league is ${isCompleted ? 'finished' : 'in progress'}`}.
              </div>
            ) : isLeagueFull ? (
              <div style={{ color: '#64748b', fontSize: '0.85rem', padding: '0.5rem 0' }}>
                All {league.total_teams} {unitLower} slots are filled. Remove a {unitLower} to invite another.
              </div>
            ) : isSingles ? (
            <div style={{ opacity: isActive ? 1 : 0.5, pointerEvents: isActive ? 'auto' : 'none' }}>
              <UserSearchInput
                placeholder="Search player by username…"
                onSelect={handleInvitePlayer}
                excludeUserIds={enteredUserIds}
                disabled={!isActive || invitingTeamId !== null}
              />
              <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '0.35rem' }}>
                Singles league: players join individually. Picking a player sends the invitation.
              </div>
            </div>
            ) : isDoubles ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', opacity: isActive ? 1 : 0.5, pointerEvents: isActive ? 'auto' : 'none' }}>
              <UserSearchInput
                placeholder="Player 1…"
                selectedUser={pairPlayer1}
                onSelect={setPairPlayer1}
                onClear={() => setPairPlayer1(null)}
                excludeUserIds={excludeForPlayer1}
              />
              <UserSearchInput
                placeholder="Player 2…"
                selectedUser={pairPlayer2}
                onSelect={setPairPlayer2}
                onClear={() => setPairPlayer2(null)}
                excludeUserIds={excludeForPlayer2}
              />
              <button
                type="button"
                onClick={handleInvitePair}
                disabled={!pairPlayer1 || !pairPlayer2 || invitingTeamId === 'pair'}
                style={{ ...btnBase, alignSelf: 'flex-start', padding: '0.35rem 0.9rem', background: 'rgba(99,102,241,0.25)', borderColor: 'rgba(99,102,241,0.6)', color: '#a78bfa' }}
              >
                {invitingTeamId === 'pair' ? 'Sending…' : '👥 Invite Pair'}
              </button>
              <div style={{ color: '#64748b', fontSize: '0.78rem' }}>
                Doubles league: each entry is a pair of two players. Or search an existing two-player team:
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search pair team by name…"
                  value={inviteSearch}
                  onChange={(e) => handleInviteSearch(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
            ) : (
            <div style={{ position: 'relative', opacity: isActive ? 1 : 0.5, pointerEvents: isActive ? 'auto' : 'none' }}>
              <input
                type="text"
                placeholder="Search team by name…"
                value={inviteSearch}
                onChange={(e) => handleInviteSearch(e.target.value)}
                style={inputStyle}
              />
              {inviteSearching && (
                <span style={{ position: 'absolute', right: '0.8rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem' }}>…</span>
              )}
            </div>
            )}
            {inviteResults.length > 0 && !isLeagueFull && !isSingles && (
              <ul style={{ margin: '0.4rem 0 0', padding: 0, listStyle: 'none', background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '0.4rem', maxHeight: '220px', overflowY: 'auto' }}>
                {inviteResults.map((team) => {
                  const isMember = teams.some((t) => (t.team_id ?? t.id) === team.id);
                  return (
                    <li key={team.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.85rem', borderBottom: '1px solid rgba(99,102,241,0.15)' }}>
                      <span style={{ color: '#e2e8f0', fontSize: '0.88rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {team.name}
                        {isMember && <span style={{ marginLeft: '0.4rem', fontSize: '0.72rem', color: '#22c55e', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.35)', borderRadius: '0.25rem', padding: '0.1rem 0.35rem' }}>In League</span>}
                      </span>
                      <button
                        onClick={() => handleInviteTeam(team.id, team.name)}
                        disabled={invitingTeamId === team.id || isMember || pendingOutgoingIds.has(team.id)}
                        style={{ ...btnBase, flexShrink: 0, background: isMember ? 'rgba(100,116,139,0.2)' : pendingOutgoingIds.has(team.id) ? 'rgba(251,191,36,0.12)' : 'rgba(99,102,241,0.25)', borderColor: isMember ? 'rgba(100,116,139,0.4)' : pendingOutgoingIds.has(team.id) ? 'rgba(251,191,36,0.4)' : 'rgba(99,102,241,0.6)', color: isMember ? '#64748b' : pendingOutgoingIds.has(team.id) ? '#fbbf24' : '#a78bfa', cursor: (isMember || pendingOutgoingIds.has(team.id)) ? 'not-allowed' : 'pointer' }}
                      >
                        {invitingTeamId === team.id ? 'Sending…' : isMember ? 'In League' : pendingOutgoingIds.has(team.id) ? 'Pending…' : 'Invite'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Right: Pending requests */}
          <div style={{ flex: '1 1 260px', minWidth: 0, padding: '1rem', background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '0.5rem' }}>
            <div style={{ fontWeight: 600, color: '#fbbf24', marginBottom: '0.6rem', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              📬 Pending Requests
              {pendingInvitations.length > 0 && (
                <span style={{ background: '#fbbf24', color: '#0f172a', borderRadius: '9999px', padding: '0.1rem 0.45rem', fontSize: '0.72rem', fontWeight: 700 }}>{pendingInvitations.length}</span>
              )}
            </div>
            {invitationsLoading ? (
              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Loading…</div>
            ) : pendingInvitations.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '0.85rem' }}>No pending requests.</div>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {pendingInvitations.map((inv) => (
                  <li key={inv.id} style={{ paddingBottom: '0.6rem', marginBottom: '0.6rem', borderBottom: '1px solid rgba(251,191,36,0.12)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.team_name}</div>
                        <div style={{ marginTop: '0.2rem', fontSize: '0.72rem', padding: '0.1rem 0.4rem', display: 'inline-block', borderRadius: '0.25rem', background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.4)', color: '#38bdf8' }}>
                          📩 Requested to join
                        </div>
                        {inv.message && (
                          <div style={{ marginTop: '0.35rem', fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic', wordBreak: 'break-word' }}>&#8220;{inv.message}&#8221;</div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                        <button
                          onClick={() => handleRespond(inv.id, 'accept', inv.team_name)}
                          disabled={respondingId === inv.id}
                          style={{ ...btnBase, background: 'rgba(34,197,94,0.2)', borderColor: 'rgba(34,197,94,0.5)', color: '#22c55e' }}
                        >
                          {respondingId === inv.id ? '…' : '✅'}
                        </button>
                        <button
                          onClick={() => handleRespond(inv.id, 'decline', inv.team_name)}
                          disabled={respondingId === inv.id}
                          style={{ ...btnBase, background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.4)', color: '#ef4444' }}
                        >
                          {respondingId === inv.id ? '…' : '❌'}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>
      )}

      {/* ── NON-HOST: request to join (public + active) ─────────────────────── */}
      {canRequestJoin && (
        <div style={{ ...panelStyle, background: 'rgba(56,189,248,0.06)', borderColor: 'rgba(56,189,248,0.25)' }}>
          <div style={{ fontWeight: 600, color: '#38bdf8', marginBottom: '0.6rem', fontSize: '0.9rem' }}>📩 Request to Join</div>

          {isLocked ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.65rem 0.85rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.4rem', fontSize: '0.87rem', color: '#fca5a5' }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>🔒</span>
              <span>Registrations are closed. This league is {isCompleted ? 'finished' : isRegClosed ? 'no longer taking entries' : 'already in progress'} and no longer accepting new {unitLower}s.</span>
            </div>
          ) : isLeagueFull ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.65rem 0.85rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.4rem', fontSize: '0.87rem', color: '#fca5a5' }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>🚫</span>
              <span>This league is full ({teams.length}/{league.total_teams} {unitLower}s). No more registrations are being accepted.</span>
            </div>
          ) : (isSingles || isDoubles) && alreadyEntered ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.65rem 0.85rem', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '0.4rem', fontSize: '0.87rem', color: '#86efac' }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>✅</span>
              <span>You are already playing in this league.</span>
            </div>
          ) : isSingles || isDoubles ? (
            <form onSubmit={handleJoinAsPlayerOrPair} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {isDoubles ? (
                <UserSearchInput
                  placeholder="Your doubles partner…"
                  selectedUser={partner}
                  onSelect={setPartner}
                  onClear={() => setPartner(null)}
                  excludeUserIds={excludeForPartner}
                />
              ) : (
                <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Singles league: you join as an individual player.</div>
              )}
              <input
                type="text"
                placeholder="Optional message to host… (200 char max)"
                value={joinMessage}
                onChange={(e) => setJoinMessage(e.target.value)}
                maxLength={200}
                style={{ ...inputStyle, border: '1px solid rgba(56,189,248,0.4)' }}
              />
              <button
                type="submit"
                disabled={requestSending || (isDoubles && !partner)}
                style={{ alignSelf: 'flex-start', padding: '0.4rem 1rem', background: 'rgba(56,189,248,0.25)', border: '1px solid rgba(56,189,248,0.5)', color: '#38bdf8', borderRadius: '0.4rem', cursor: requestSending ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
              >
                {requestSending ? 'Sending…' : isDoubles ? '👥 Request to Join as a Pair' : '🙋 Request to Join'}
              </button>
            </form>
          ) : alreadyRegisteredTeams.length > 0 ? (
            /* User already has a team enrolled — no second registration allowed */
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.65rem 0.85rem', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '0.4rem', fontSize: '0.87rem', color: '#86efac' }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>✅</span>
              <span>
                <strong>{alreadyRegisteredTeams[0].name}</strong> is already registered in this league.
                {alreadyRegisteredTeams.length > 1
                  ? ' Only one team per league is allowed.'
                  : ' Only one team per league is allowed.'}
              </span>
            </div>
          ) : myTeams.length === 0 ? (
            /* No sport-matching eligible teams */
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
              {league?.sport_type
                ? `No eligible ${league.sport_type} teams. Create a ${league.sport_type} team to request to join.`
                : 'No eligible teams. Create a team first to request to join.'}
            </div>
          ) : (
            /* Show sport-filtered team dropdown */
            <form onSubmit={handleJoinRequest} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                required
                style={{ ...inputStyle, border: '1px solid rgba(56,189,248,0.4)' }}
              >
                <option value="">Select your team…</option>
                {myTeams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <div>
                <input
                  type="text"
                  placeholder="Optional message to host… (200 char max)"
                  value={joinMessage}
                  onChange={(e) => setJoinMessage(e.target.value)}
                  maxLength={200}
                  style={{ ...inputStyle, border: '1px solid rgba(56,189,248,0.4)' }}
                />
                <div style={{ textAlign: 'right', fontSize: '0.72rem', marginTop: '0.2rem', color: joinMessage.length > 160 ? '#f59e0b' : '#475569' }}>
                  {joinMessage.length}/200
                </div>
              </div>
              <button
                type="submit"
                disabled={requestSending || !selectedTeamId}
                style={{ alignSelf: 'flex-start', padding: '0.4rem 1rem', background: requestSending ? 'rgba(56,189,248,0.15)' : 'rgba(56,189,248,0.25)', border: '1px solid rgba(56,189,248,0.5)', color: '#38bdf8', borderRadius: '0.4rem', cursor: requestSending ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
              >
                {requestSending ? 'Sending…' : '📩 Send Join Request'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* ── Enrolled Teams list ─────────────────────────────────────────────── */}
      {teams.length === 0 ? (
        <div className="empty-content">
          <p>No {unitLower}s enrolled yet.</p>
          {canManageLeagues && isActive && <p>Use the invite search above to get started.</p>}
        </div>
      ) : (
        <div className="teams-grid">
          {teams.map((team, index) => {
            const name = team.team_name || team.name;
            const sport = team.sport_name || team.sport_type || league?.sport_type || 'Sport';
            const isPlayerEntry = team.participant_type === 'user';
            const players = team.member_count || team.active_members_count || 0;
            return (
              <EntityCard
                key={team.id || team.team_id || index}
                title={name}
                logoUrl={isPlayerEntry ? team.owner_avatar : (team.logo_url || team.logo)}
                logoAlt={isPlayerEntry ? name : `${name} logo`}
                badges={[
                  { label: sport, tone: 'sport' },
                  ...(!isPlayerEntry && team.roster_complete === false
                    ? [{ label: 'Short roster', tone: 'warning', title: 'Fewer active players than the sport needs' }]
                    : []),
                  ...(isWithdrawn(team) ? [{ label: 'Withdrawn', tone: 'muted' }] : []),
                ]}
                rows={[
                  isPlayerEntry
                    ? { key: 'entry', icon: '🙋', label: 'Entry', value: `Singles player${team.owner_username ? ` (@${team.owner_username})` : ''}` }
                    : { key: 'members', icon: '👥', label: isDoubles ? 'Players' : 'Members', value: membersLine(players, onCourtSize(team, league?.sport_type)) },
                  { key: 'location', icon: '📍', label: 'Location', value: team.location || team.city || 'N/A' },
                ]}
                corner={canWithdraw(team) ? (
                  <button
                    type="button"
                    className="team-withdraw-btn"
                    onClick={(e) => { e.stopPropagation(); setWithdrawing(team); }}
                    aria-label={`Withdraw ${name} from the league`}
                  >
                    Withdraw
                  </button>
                ) : canRemoveEntries && (
                  <button
                    type="button"
                    className="team-remove-btn"
                    onClick={(e) => { e.stopPropagation(); setRemoveConfirm({ isOpen: true, team, message: '', isLoading: false }); }}
                    title={`Remove ${unitLower} from league`}
                    aria-label={`Remove ${name} from league`}
                  >
                    <FaTrash />
                  </button>
                )}
                onClick={() => openLeagueEntry(navigate, league, team, 'teams')}
              />
            );
          })}
        </div>
      )}

      {teams.length >= league.total_teams && (
        <div className="teams-full-message"><p>✓ All {unitLower} slots filled!</p></div>
      )}

      {withdrawing && (
        <WithdrawEntryDialog
          league={league}
          entryKey={withdrawing.participant_type === 'user' ? withdrawing.id : (withdrawing.team_id || withdrawing.id)}
          name={withdrawing.team_name || withdrawing.name}
          unitLower={unitLower}
          onClose={() => setWithdrawing(null)}
          onDone={(message) => {
            setWithdrawing(null);
            showFeedback('success', message);
            fetchLeagueTeams?.();
          }}
        />
      )}

      {/* Remove Team Confirmation — custom dialog with optional message */}
      {removeConfirm.isOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closeRemoveConfirm}
        >
          <div
            style={{ background: '#0f172a', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '0.75rem', padding: '1.5rem', width: '100%', maxWidth: '400px', color: '#e2e8f0' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, color: '#ef4444', fontSize: '1rem' }}>❌ Remove {unit}</h3>
              <button onClick={closeRemoveConfirm} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.2rem', cursor: 'pointer' }}>×</button>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#cbd5e1', marginBottom: '0.75rem' }}>
              Remove <strong>{removeConfirm.team?.team_name || removeConfirm.team?.name}</strong> from this league?{' '}
              {removeConfirm.team?.games_count > 0
                ? `Its ${removeConfirm.team.games_count} scheduled game${removeConfirm.team.games_count === 1 ? ' is' : 's are'} removed too.`
                : 'It has no scheduled games.'}{' '}
              This action cannot be undone.
            </p>
            {league.fee_mode && league.fee_mode !== 'none' && (
              <p data-testid="remove-refund-note" style={{ fontSize: '0.82rem', color: '#fcd34d', marginBottom: '0.75rem' }}>
                If they paid the season fee, it is refunded to them automatically.
              </p>
            )}
            <label style={{ fontSize: '0.82rem', color: '#94a3b8', display: 'block', marginBottom: '0.3rem' }}>Optional message to the {unitLower}</label>
            <textarea
              value={removeConfirm.message}
              onChange={(e) => setRemoveConfirm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="e.g. League schedule conflict, inactivity, etc.…"
              maxLength={200}
              rows={3}
              style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '0.4rem', color: '#e2e8f0', fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ textAlign: 'right', fontSize: '0.72rem', marginTop: '0.2rem', marginBottom: '0.8rem', color: removeConfirm.message.length > 160 ? '#f59e0b' : '#475569' }}>
              {removeConfirm.message.length}/200
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button
                onClick={closeRemoveConfirm}
                style={{ padding: '0.4rem 1rem', background: 'transparent', border: '1px solid rgba(100,116,139,0.4)', color: '#94a3b8', borderRadius: '0.4rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveTeam}
                disabled={removeConfirm.isLoading}
                style={{ padding: '0.4rem 1rem', background: 'rgba(239,68,68,0.8)', border: '1px solid rgba(239,68,68,0.6)', color: '#fff', borderRadius: '0.4rem', cursor: removeConfirm.isLoading ? 'not-allowed' : 'pointer', fontWeight: 600 }}
              >
                {removeConfirm.isLoading ? 'Removing…' : `Remove ${unit}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeagueTeamsTab;
